import ExpiredOverlay from "$/components/ExpiredOverlay";
import constants from "@blackink/core/categoryConstants";
import { KeyboardArrowDown } from "@mui/icons-material";
import IdeaIcon from "@mui/icons-material/EmojiObjects";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as Sentry from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import analytics from "../../util/analytics";
import { useAuth } from "../../util/auth";
import { createJob, subscribeJob } from "../../util/db";
import supabase from "../../util/supabase";

const styleGroups = [
  {
    id: "black",
    label: "Black Ink",
    styles: ["blackwork", "dotwork", "black-grey"],
  },
  {
    id: "minimalist",
    label: "Minimalist",
    styles: ["minimalist", "sketch"],
  },
  {
    id: "color",
    label: "Color",
    styles: ["neotraditional", "new-school", "realism", "traditional"],
  },
];

interface StyleGroup {
  id: string;
  label: string;
  styles: string[];
}

interface Job {
  status: string;
  result: {
    prompt: string;
    style: string;
  }[];
}

interface LocationState {
  styleGroup: StyleGroup;
  interests: string;
  occupation: string;
  favoriteThings: string;
  job: Job;
}

const CreateIdeaTool = () => {
  const { state } = useLocation() as { state: LocationState };

  const [interests, setInterests] = useState("");
  const [occupation, setOccupation] = useState("");
  const [favoriteThings, setFavoriteThings] = useState("");
  const [styleGroup, setStyleGroup] = useState(styleGroups[0]);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [shouldCreate, setShouldCreate] = useState(false);

  const auth = useAuth();
  const navigate = useNavigate();

  const isGenerating =
    job && (job.status === "INITIAL" || job.status === "IN_PROGRESS");
  const creditsDepleted = auth.user && auth.user.credits <= 0 && !auth.isProUser;

  useEffect(() => {
    if (state && state.styleGroup) {
      setStyleGroup(styleGroup);
    }
    if (state && state.interests) {
      setInterests(state.interests);
    }
    if (state && state.occupation) {
      setOccupation(state.occupation);
    }
    if (state && state.favoriteThings) {
      setFavoriteThings(state.favoriteThings);
    }
    if (state && state.job) {
      setJob(state.job);
    }
  }, [state, styleGroup]);

  const handleJobEvent = (jobEvent) => {
    setJob(jobEvent.new);
    // No need to refresh user here because we're not using credits

    if (jobEvent.new.status === "SUCCESS") {
      // Store state in history for navigating back
      navigate("/tattoo-ideas-generator/result", {
        state: {
          interests,
          occupation,
          favoriteThings,
          styleGroup,
          job: jobEvent.new,
        },
      });
      supabase.removeAllChannels();
    }
  };

  const handleError = (err) => {
    Sentry.captureException(err);
    setJob({ status: "ERROR" });
    supabase.removeAllChannels();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!interests || !occupation || !favoriteThings) {
      // Show errors
      setError("Please fill out all fields");
      return;
    }

    if (!auth.user) {
      // Join anonymously, passthrough user because hooks won't have updated user yet
      await auth.signinAnonymously();
    }

    setShouldCreate(true);
    setError(null);
    setJob({ status: "INITIAL" });
  };

  const runCreate = async () => {
    if (!auth.user) {
      // Show errors
      setError("Please sign in to generate ideas");
      return;
    }

    analytics.track("create_ideas");

    try {
      const response = await createJob(
        auth.user.id,
        {
          interests,
          occupation,
          favoriteThings,
          selectedStyles: styleGroup.styles,
        },
        "idea",
      );
      const ideaJob = response[0];

      subscribeJob(ideaJob.id, handleJobEvent);
    } catch (err) {
      handleError(err);
    }
  };

  const createFromIdeas = (idea) => {
    analytics.track("create_from_idea", {
      prompt: idea.prompt,
      style: idea.style,
    });

    navigate("/", {
      state: {
        prompt: idea.prompt,
        style: idea.style,
      },
    });
  };

  useEffect(() => {
    if (shouldCreate && auth.user) {
      runCreate();
      setShouldCreate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreate, auth.user]);

  return (
    <>
      <Box mt={1} mb={2}>
        <Typography variant="h5" variantMapping={{ h3: "h1" }} gutterBottom>
          Tattoo Idea Generator
        </Typography>
        <Box
          sx={{
            "& .MuiTextField-root": { my: 1 },
            "& .MuiButton-root": { my: 1 },
          }}
        >
          {job && job.status === "SUCCESS" ? (
            <>
              <Grid container spacing={2}>
                {job.result.map((item, i) => (
                  <Grid item xs={6} md={4} key={i}>
                    <Card variant="outlined" key={i}>
                      <CardContent sx={{ pb: "0 !important" }}>
                        <Typography variant="subtitle3" component={"div"}>
                          {constants.CATEGORY_DESC[item.style].name}
                        </Typography>
                        <Typography sx={{ fontSize: 18, minHeight: 80 }} gutterBottom>
                          {item.prompt}
                        </Typography>

                        <Button
                          fullWidth
                          color="primary"
                          variant="outlined"
                          onClick={() => createFromIdeas(item)}
                        >
                          Generate
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Button
                onClick={() => setJob(null)}
                fullWidth
                startIcon={<ModelTrainingIcon />}
              >
                Try Again
              </Button>
            </>
          ) : (
            <Box position="relative">
              {(creditsDepleted || auth.isAnonymousUser) && (
                <ExpiredOverlay disableAuthPopup allowFree />
              )}
              <Typography variant="subtitle3">Style Options</Typography>
              <Stack direction="row" spacing={2}>
                {styleGroups.map((group, i) => (
                  <Button
                    key={group.id}
                    variant="outlined"
                    color={group.id === styleGroup.id ? "inherit" : "info"}
                    onClick={() => setStyleGroup(group)}
                    disabled={isGenerating}
                  >
                    {group.label}
                  </Button>
                ))}
              </Stack>
              <Typography component="div" variant="subtitle3" mt={1} mb={1}>
                Tell us about yourself and your interests
              </Typography>

              <TextField
                InputLabelProps={{ shrink: true }}
                label="Job/Occupation"
                placeholder="e.g. student, barista, engineer, veteran, teacher, etc."
                fullWidth
                variant="outlined"
                value={occupation}
                onChange={(e) => setOccupation(e.target.value)}
                disabled={isGenerating}
                required
                error={error && !occupation}
              />
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Interests/Passions/Hobbies"
                placeholder="e.g. music, hiking, coffee, etc."
                fullWidth
                variant="outlined"
                value={interests}
                onChange={(e) => setInterests(e.target.value)}
                multiline
                rows={4}
                disabled={isGenerating}
                required
                error={error && !interests}
              />
              <TextField
                InputLabelProps={{ shrink: true }}
                label="Favorite Things"
                placeholder="e.g. animals, objects, places, etc."
                fullWidth
                variant="outlined"
                value={favoriteThings}
                onChange={(e) => setFavoriteThings(e.target.value)}
                multiline
                rows={4}
                disabled={isGenerating}
                required
                error={error && !favoriteThings}
              />
              {error && (
                <Typography variant="body1" color="error">
                  {error}
                </Typography>
              )}
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={onSubmit}
                size="large"
                fullWidth
                loading={isGenerating}
                startIcon={<IdeaIcon />}
              >
                <span>{isGenerating ? "Generating Ideas..." : "Generate Ideas"}</span>
              </LoadingButton>
            </Box>
          )}

          <Box textAlign="center" pt={2}>
            <Button
              component={HashLink}
              to="#content"
              variant="text"
              color="inherit"
              size="large"
              sx={{ marginTop: 2 }}
            >
              <Stack direction="column" alignItems="center">
                <div>Explore By Category</div>
                <KeyboardArrowDown fontSize="large" />
              </Stack>
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CreateIdeaTool;
